import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { dispatch, store } from 'store';
import { fetchUnreadCounts, type ChatScope, type SystemCounts, type ViewCountCell } from 'api/unread-counts';
import { buildUnreadCountsRequest } from 'utils/build-unread-counts-request';

export type { ViewCountCell, SystemCounts } from 'api/unread-counts';

interface ViewCountsState {
  system: SystemCounts;
  /** מונה תצוגת «בוטים» — שיחות שמוחזקות כרגע אצל בוט */
  bots: ViewCountCell | null;
  /** תא «לא משויכות» (סטטוס 1 וגם בלי manager) — null כשהשרת עוד לא שולח אותו */
  unassigned: ViewCountCell | null;
  custom: Record<string, ViewCountCell>;
  lastUpdatedIso: string | null;
  loading: boolean;
}

const initialState: ViewCountsState = {
  system: {},
  bots: null,
  unassigned: null,
  custom: {},
  lastUpdatedIso: null,
  loading: false,
};

const viewCountsSlice = createSlice({
  name: 'viewCounts',
  initialState,
  reducers: {
    storeSetViewCounts: (
      state,
      action: PayloadAction<{
        system: SystemCounts;
        bots?: ViewCountCell | null;
        unassigned?: ViewCountCell | null;
        custom: Record<string, ViewCountCell>;
        lastUpdatedIso: string;
      }>,
    ) => {
      state.system = action.payload.system;
      state.bots = action.payload.bots ?? null;
      state.unassigned = action.payload.unassigned ?? null;
      state.custom = action.payload.custom;
      state.lastUpdatedIso = action.payload.lastUpdatedIso;
    },
    storeSetViewCountsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    storeClearViewCounts: () => initialState,
    storeDecrementViewCountUnread: (
      state,
      action: PayloadAction<{
        flowStatusCode?: number | null;
        scopes: ChatScope[];
        /** תצוגה מותאמת שהשורה ידועה כשייכת אליה (נטענה ממנה) — גם התג שלה יורד */
        customViewId?: string | null;
        /** השורה אצל בוט פעיל — גם תג «בוטים» יורד */
        isBot?: boolean;
        /** השורה "לא משויכת" (סטטוס 1 בלי manager) — גם התא הייעודי יורד */
        isUnassigned?: boolean;
      }>,
    ) => {
      const decrement = (cell: ViewCountCell | null | undefined) => {
        if (cell && cell.unread > 0) cell.unread -= 1;
      };

      //* חקירת 4.11: עד עכשיו ירדו רק תאי המערכת — תג התצוגה המותאמת ותג הבוטים נשארו
      //* על הערך הישן עד תשובת השרת, בעוד השורה כבר לבנה ותגי המערכת כבר ירדו.
      if (action.payload.customViewId) decrement(state.custom[action.payload.customViewId]);
      if (action.payload.isBot) decrement(state.bots);
      if (action.payload.isUnassigned) decrement(state.unassigned);

      const statusKey =
        action.payload.flowStatusCode != null ? String(action.payload.flowStatusCode) : null;
      if (!statusKey) return;

      for (const scope of action.payload.scopes) {
        decrement(state.system[scope]?.[statusKey]);
      }
    },
  },
});

export default viewCountsSlice.reducer;
export const {
  storeSetViewCounts,
  storeSetViewCountsLoading,
  storeClearViewCounts,
  storeDecrementViewCountUnread,
} = viewCountsSlice.actions;

/**
 * מושך מוני תצוגות מ-Node ושומר ל-store. שגיאות לא מרוקנות את ה-state
 * (stale-but-visible). השתמש ב-scheduleUnreadCountsRefresh לרענון מאירועי socket.
 */
export const getUnreadCounts = async (): Promise<void> => {
  const req = buildUnreadCountsRequest(store.getState());
  if (!req) return; // המשתמש עוד לא מזוהה — אין מה לבקש

  dispatch(storeSetViewCountsLoading(true));
  try {
    const data = await fetchUnreadCounts(req);
    if (data && data.system) {
      dispatch(
        storeSetViewCounts({
          system: data.system,
          bots: data.bots ?? null,
          unassigned: data.unassigned ?? null,
          custom: data.custom || {},
          lastUpdatedIso: data.meta?.computedAtIso || new Date().toISOString(),
        }),
      );
    }
  } finally {
    dispatch(storeSetViewCountsLoading(false));
  }
};
